import {
  FEED_DATASETS,
  FEED_RUN_STATUSES,
  type FeedProviderId,
  type FeedProviderStatus,
} from '@fruuit/shared'
import {
  Button,
  Callout,
  Card,
  LoadingRegion,
  PageHeader,
  Select,
  Tabs,
  useToast,
} from '@fruuit/ui'
import { ExternalLink, Play } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useSearchParams } from 'react-router'
import { errorMessage } from '../../api/client'
import { useSession } from '../../auth/session-context'
import { useFeedRuns, useFeeds, useRunFeed } from '../../features/feeds/api'
import { HealthBadge, LicenceBadges } from '../../features/feeds/badges'
import { formatDateTime, formatPercent, formatRelative, plural } from '../../features/feeds/format'
import { RunLegend, RunSparkline } from '../../features/feeds/RunSparkline'
import { RunTable } from '../../features/feeds/RunTable'
import { useCursorPager } from '../../features/feeds/useCursorPager'
import { useTitle } from '../../lib/useTitle'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...FEED_RUN_STATUSES.values.map((value) => ({ value, label: FEED_RUN_STATUSES.labels[value] })),
]

function RunNowButton({ provider }: { provider: FeedProviderStatus }) {
  const { toast } = useToast()
  const run = useRunFeed(provider.id)
  return (
    <Button
      icon={<Play />}
      loading={run.isPending}
      disabled={!provider.enabled}
      onClick={() => {
        run
          .mutateAsync(undefined)
          .then((result) => {
            toast({
              tone: result.outcome === 'ran' ? 'success' : 'info',
              title:
                result.outcome === 'ran'
                  ? 'Run finished'
                  : result.outcome === 'not_ready'
                    ? 'Delivery not ready yet'
                    : 'Run skipped',
              message: result.message,
            })
          })
          .catch((error: unknown) => {
            toast({ tone: 'error', title: 'Run not started', message: errorMessage(error) })
          })
      }}
    >
      Run now
    </Button>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold text-sage">{label}</dt>
      <dd className="mt-1 text-sm text-ink">{children}</dd>
    </div>
  )
}

function ProviderDetail({
  provider,
  canOperate,
}: {
  provider: FeedProviderStatus
  canOperate: boolean
}) {
  const [status, setStatus] = useState('')
  const pager = useCursorPager()
  const runs = useFeedRuns(provider.id, status, pager.cursor)

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={provider.name}
        eyebrow={provider.licence_label}
        actions={
          canOperate ? (
            <RunNowButton provider={provider} />
          ) : (
            <span className="max-w-[16rem] text-xs text-sage">
              Manual re-runs are limited to platform operators.
            </span>
          )
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <HealthBadge health={provider.health} />
          <LicenceBadges simulated={provider.simulated} licence={provider.licence_status} />
        </div>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-ink-soft">
          {provider.description}
        </p>

        <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
          <Fact label="Schedule">
            {provider.schedule_label}
            <span className="block text-xs text-sage">
              Late after {provider.late_after_minutes} min
            </span>
          </Fact>
          <Fact label="Next run">
            {provider.next_due_at ? formatRelative(provider.next_due_at) : 'Not scheduled'}
            <span className="block text-xs text-sage">{formatDateTime(provider.next_due_at)}</span>
          </Fact>
          <Fact label="Success rate">
            <span className="font-semibold tabular-nums">
              {formatPercent(provider.success_rate_24h)}
            </span>{' '}
            <span className="text-xs text-sage">24 h</span>
            <span className="block">
              <span className="font-semibold tabular-nums">
                {formatPercent(provider.success_rate_7d)}
              </span>{' '}
              <span className="text-xs text-sage">7 days, {plural(provider.runs_7d, 'run')}</span>
            </span>
          </Fact>
          <Fact label="Open alerts">
            <span
              className={`font-semibold tabular-nums ${provider.open_alerts ? 'text-attention' : ''}`}
            >
              {provider.open_alerts}
            </span>
            <span className="block text-xs text-sage">
              {plural(provider.signals_7d, 'signal')} in 7 days
            </span>
          </Fact>
          <Fact label="Last success">{formatDateTime(provider.last_success_at, 'None yet')}</Fact>
          <Fact label="Identifies companies by">{provider.identifier_types.join(', ') || '—'}</Fact>
          <Fact label="Datasets">
            {provider.datasets.map((dataset) => FEED_DATASETS.labels[dataset]).join(', ')}
          </Fact>
          <Fact label="Last 7 days">
            <RunSparkline days={provider.days} label={provider.name} height={32} />
          </Fact>
        </dl>
        <RunLegend className="mt-3 justify-end" />

        <Callout tone="info" title="Attribution" className="mt-5">
          <p>{provider.attribution}</p>
          <a
            href={provider.terms_url}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-1 inline-flex items-center gap-1 font-semibold underline"
          >
            Terms of use
            <ExternalLink aria-hidden="true" className="size-3.5" />
          </a>
        </Callout>
      </Card>

      <section aria-labelledby={`runs-${provider.id}`}>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div className="max-w-2xl">
            <h2 id={`runs-${provider.id}`} className="text-lg font-semibold text-ink">
              Run timeline
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-sage">
              Every run is recorded, including failures. Failed, partial and late runs leave
              coverage gaps, and zero results never means there is no concern.
            </p>
          </div>
          <Select
            label="Status"
            className="w-48"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value)
              pager.reset()
            }}
          />
        </div>
        {runs.isError ? (
          <Callout tone="danger" role="alert">
            {errorMessage(runs.error)}
          </Callout>
        ) : (
          <RunTable
            runs={runs.data?.items ?? []}
            loading={runs.isPending || runs.isPlaceholderData}
            pagination={{
              mode: 'server',
              hasPrevious: pager.hasPrevious,
              hasNext: Boolean(runs.data?.next_cursor),
              onPrevious: pager.previous,
              onNext: () => pager.next(runs.data?.next_cursor ?? null),
              summary: `Page ${pager.page}`,
            }}
          />
        )}
      </section>
    </div>
  )
}

/** M-03 / M-05 Data feeds: provider detail, run timeline, manual re-run (⚙ operators). */
export function FeedsPage() {
  useTitle('Data feeds', 'Admin')
  const me = useSession()
  const canOperate = me.platform_role === 'platform_operator'
  const feeds = useFeeds()
  const [params, setParams] = useSearchParams()
  const providers = feeds.data?.providers ?? []
  const requested = params.get('provider')
  const selected =
    providers.find((provider) => provider.id === requested)?.id ?? providers[0]?.id ?? ''

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Data feeds"
        description="Run history and coverage gaps for each data source."
      />
      {feeds.isPending ? (
        <LoadingRegion label="Loading data feeds" />
      ) : feeds.isError ? (
        <Callout tone="danger" role="alert">
          {errorMessage(feeds.error)}
        </Callout>
      ) : (
        <Tabs
          label="Data sources"
          value={selected}
          onValueChange={(id) =>
            setParams(
              (current) => {
                const next = new URLSearchParams(current)
                next.set('provider', id as FeedProviderId)
                return next
              },
              { replace: true },
            )
          }
          items={providers.map((provider) => ({
            id: provider.id,
            label: provider.name,
            count: provider.open_alerts || undefined,
            content: (
              <ProviderDetail key={provider.id} provider={provider} canOperate={canOperate} />
            ),
          }))}
        />
      )}
    </>
  )
}
